<template>
  <div class="login-page">
    <header class="site-header">
      <div class="brand">
        <img
          class="logo"
          src="@/assets/img/echoingocean_logo.png"
          alt="EchoingOcean logo"
        />
        <span class="brand-name">Remote Command Platform</span>
      </div>
      <div class="header-meta"></div>
    </header>

    <main class="content">
      <section class="login-card">
        <div class="card-header">
          <h1>忘記密碼</h1>
          <p>{{ step === "request" ? "請輸入帳號，我們將寄送驗證碼到您的註冊 Email。" : "請輸入驗證碼進行驗證" }}</p>
        </div>

        <form class="login-form" @submit.prevent>
          <template v-if="step === 'request'">
            <label class="field">
              <span class="label">帳號</span>
              <input
                v-model="form.account"
                type="text"
                autocomplete="username"
                placeholder="請輸入帳號"
              />
            </label>
          </template>

          <template v-else>
            <label class="field">
              <span class="label">驗證碼</span>
              <input
                v-model="form.code"
                type="text"
                autocomplete="one-time-code"
                placeholder="請輸入驗證碼"
              />
            </label>
          </template>

          <div v-if="errorMessage" class="error-message" role="alert">
            {{ errorMessage }}
          </div>
          <div v-if="successMessage" class="success-message" role="status">
            {{ successMessage }}
          </div>

          <div class="form-footer">
            <router-link class="forgot" to="/login">返回登入</router-link>
            <button class="primary-button" type="button" :disabled="isSubmitting" @click="handleSubmit">
              {{ isSubmitting ? "處理中..." : step === "request" ? "送出驗證碼" : "驗證" }}
            </button>
          </div>
        </form>
      </section>
    </main>

    <footer class="site-footer">
      <div>Contact: 07-2361578</div>
      <div>Email: support@echoingocean.com</div>
      <div>{{ $buildLabel }}</div>
    </footer>
  </div>
</template>

<script>
import { CHECK_VERIFY_CODE, SEND_VERIFY_CODE } from "@/utils/requestUrl";
import { setRefreshToken, setToken } from "@/utils/sessionStoraggeUtil";

export default {
  name: "PasswordForgotView",
  data() {
    return {
      form: {
        account: "",
        code: ""
      },
      step: "request",
      isSubmitting: false,
      errorMessage: "",
      successMessage: ""
    };
  },
  methods: {
    getApiBase() {
      if (window._env_ && window._env_.VUE_APP_REQUEST_DOMAIN) {
        return window._env_.VUE_APP_REQUEST_DOMAIN;
      }
      return "";
    },
    async handleSubmit() {
      this.errorMessage = "";
      this.successMessage = "";

      if (!this.form.account) {
        this.errorMessage = "請輸入帳號。";
        return;
      }

      if (this.step === "request") {
        await this.sendVerifyCode();
        return;
      }
      await this.checkVerifyCode();
    },
    async sendVerifyCode() {
      this.isSubmitting = true;
      try {
        const response = await fetch(this.getApiBase() + SEND_VERIFY_CODE, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            Account: this.form.account
          })
        });
        let payload = null;
        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        if (!response.ok) {
          this.errorMessage = (payload && payload.message) || "送出驗證碼失敗，請稍後再試。";
          return;
        }
        if (payload && payload.status === "failure") {
          this.errorMessage = payload.message || "送出驗證碼失敗，請確認帳號。";
          return;
        }

        this.step = "verify";
        this.successMessage = (payload && payload.message) || "驗證碼已送出，請至 Email 收信。";
      } catch (error) {
        this.errorMessage = "送出驗證碼失敗，請稍後再試。";
      } finally {
        this.isSubmitting = false;
      }
    },
    async checkVerifyCode() {
      if (!this.form.code) {
        this.errorMessage = "請輸入驗證碼。";
        return;
      }

      this.isSubmitting = true;
      try {
        const response = await fetch(this.getApiBase() + CHECK_VERIFY_CODE, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            Account: this.form.account,
            VerifyCode: this.form.code
          })
        });
        let payload = null;
        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        if (!response.ok) {
          this.errorMessage = (payload && payload.message) || "驗證失敗，請稍後再試。";
          return;
        }
        if (payload && payload.status === "failure") {
          this.errorMessage = payload.message || "驗證失敗，請確認驗證碼。";
          return;
        }

        if (payload && payload.data && payload.data.token) {
          const accessToken = payload.data.token.accessToken;
          const refreshToken = payload.data.token.refreshToken;
          if (accessToken) {
            setToken(accessToken);
          }
          if (refreshToken) {
            setRefreshToken(refreshToken);
          }
        }

        this.$router.push({
          name: "passwordReset",
          params: {
            account: this.form.account
          }
        });
      } catch (error) {
        this.errorMessage = "驗證失敗，請稍後再試。";
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>
:root {
  --navy: #0b1a23;
  --deep-blue: #102437;
  --teal: #1ea6a8;
  --ice: #f2f7f7;
  --sand: #f5e6c5;
  --glow: rgba(30, 166, 168, 0.2);
}

.login-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  color: #f6fafb;
  font-family: var(--font-ui);
  background-image: url("~@/assets/img/homepage_bg.png");
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;
}

.site-header,
.site-footer,
.content {
  position: relative;
  z-index: 1;
}

.site-header {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px 56px 16px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.logo {
  width: 208px;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 0 12px rgba(30, 166, 168, 0.5));
}

.brand-name {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 1px;
  white-space: nowrap;
  text-align: center;
}

.header-meta {
  font-size: 0.9375rem;
  color: rgba(246, 250, 251, 0.7);
  letter-spacing: 2px;
  text-transform: uppercase;
}

.content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px 48px;
}

.login-card {
  width: min(520px, 92vw);
  background: linear-gradient(160deg, rgba(16, 36, 55, 0.9), rgba(7, 18, 27, 0.9));
  border-radius: 24px;
  padding: 36px 40px 32px;
  box-shadow: 0 24px 60px rgba(3, 10, 16, 0.6);
  border: 1px solid rgba(245, 230, 197, 0.2);
  backdrop-filter: blur(8px);
}

.card-header h1 {
  margin: 0 0 8px;
  font-size: 32px;
  font-weight: 600;
}

.card-header p {
  margin: 0 0 28px;
  color: rgba(246, 250, 251, 0.7);
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label {
  font-size: 0.875rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: rgba(246, 250, 251, 0.7);
}

input {
  height: 46px;
  border-radius: 12px;
  border: 1px solid rgba(246, 250, 251, 0.15);
  background: rgba(11, 26, 35, 0.8);
  color: #f6fafb;
  padding: 0 14px;
  font-size: 1rem;
}

input::placeholder {
  color: rgba(246, 250, 251, 0.5);
}

input:focus {
  outline: none;
  border-color: rgba(30, 166, 168, 0.8);
  box-shadow: 0 0 0 3px var(--glow);
}

.error-message {
  font-size: 0.875rem;
  color: #ffb3b3;
  background: rgba(255, 80, 80, 0.12);
  border: 1px solid rgba(255, 80, 80, 0.35);
  border-radius: 10px;
  padding: 10px 12px;
  line-height: 1.4;
}

.success-message {
  font-size: 0.875rem;
  color: #a6f1dc;
  background: rgba(16, 143, 104, 0.16);
  border: 1px solid rgba(16, 143, 104, 0.35);
  border-radius: 10px;
  padding: 10px 12px;
  line-height: 1.4;
}

.form-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
}

.forgot {
  color: var(--sand);
  text-decoration: none;
  font-size: 0.9375rem;
}

.forgot:hover {
  text-decoration: underline;
}

.primary-button {
  border: none;
  border-radius: 14px;
  padding: 12px 26px;
  background: linear-gradient(120deg, #1ea6a8, #0f7e7f);
  color: #f6fafb;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 12px 24px rgba(30, 166, 168, 0.35);
}

.primary-button:hover {
  transform: translateY(-1px);
}

.primary-button:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.site-footer {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding: 16px 56px 24px;
  font-size: 1rem;
  color: rgba(246, 250, 251, 0.6);
}

@media (max-width: 720px) {
  .site-header,
  .site-footer {
    padding-left: 24px;
    padding-right: 24px;
  }

  .site-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .brand {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .brand-name {
    position: static;
    transform: none;
    font-size: 1.1rem;
    white-space: normal;
  }

  .login-card {
    padding: 28px 24px;
  }

  .form-footer {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .primary-button {
    width: 100%;
  }
}
</style>
